/**
 * PermissionDenied Component
 *
 * UI for displaying permission denied states:
 * - Shows what permissions are required
 * - Explains the user's current role
 * - Provides navigation options
 *
 * @security SEC-001: Role-Based Access Control Implementation
 * @priority P0 CRITICAL
 *
 * Peer Review Required: Gemini 3 Pro + GPT 5.1
 */

import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PermissionDeniedProps } from '../../types/auth';
import { getPermissionDescription, ROLE_DISPLAY_NAMES } from '../../types/auth';

// ============================================================================
// Icons
// ============================================================================

const ShieldExclamationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z"
    />
  </svg>
);

const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
    />
  </svg>
);

const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
    />
  </svg>
);

const LockClosedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
    />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

/**
 * PermissionDenied - Full-page or inline permission denied display
 *
 * @example
 * ```tsx
 * // Basic usage
 * <PermissionDenied
 *   title="Access Denied"
 *   message="You don't have permission to view this page."
 * />
 *
 * // With details
 * <PermissionDenied
 *   requiredPermissions={['admin:view', 'admin:edit']}
 *   requiredRole="admin"
 *   currentRole="analyst"
 *   showBackButton
 *   showHomeButton
 * />
 * ```
 */
const PermissionDenied: React.FC<PermissionDeniedProps> = memo(({
  title = 'Access Denied',
  message,
  requiredPermissions,
  requiredRole,
  currentRole,
  showBackButton = true,
  showHomeButton = true,
  customAction,
}) => {
  const navigate = useNavigate();

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Generate default message if not provided
  const displayMessage = message || generateDefaultMessage(
    requiredPermissions,
    requiredRole,
    currentRole
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-red-500/20 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative px-8 pt-8 pb-6 bg-gradient-to-r from-red-500/10 to-orange-500/10">
            {/* Decorative background */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(239,68,68,0.1),transparent_70%)]" />

            {/* Icon */}
            <div className="relative flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center">
                <ShieldExclamationIcon className="w-10 h-10 text-red-400" />
              </div>
            </div>

            {/* Title */}
            <h1 className="relative text-2xl font-bold text-center text-white mb-2">
              {title}
            </h1>

            {/* Message */}
            <p className="relative text-center text-slate-400 text-sm leading-relaxed">
              {displayMessage}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-6 space-y-4">
            {/* Required Permissions */}
            {requiredPermissions && requiredPermissions.length > 0 && (
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <LockClosedIcon className="w-4 h-4" />
                  Required Permissions
                </h3>
                <ul className="space-y-2">
                  {requiredPermissions.map((permission) => (
                    <li
                      key={permission}
                      className="flex items-center gap-2 text-sm text-slate-300"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      {getPermissionDescription(permission)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Role Comparison */}
            {requiredRole && (
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Role Requirements
                </h3>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex flex-col">
                    <span className="text-slate-500 text-xs">Required</span>
                    <span className="text-red-400 font-medium">
                      {ROLE_DISPLAY_NAMES[requiredRole]}
                    </span>
                  </div>
                  <div className="text-slate-600 text-xl">→</div>
                  <div className="flex flex-col text-right">
                    <span className="text-slate-500 text-xs">Your Role</span>
                    <span className="text-slate-300 font-medium">
                      {currentRole ? ROLE_DISPLAY_NAMES[currentRole] : 'Not logged in'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Admin Note */}
            <p className="text-xs text-center text-slate-500">
              If you believe this is an error, please contact your system administrator.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="px-8 pb-8">
            {customAction ? (
              customAction
            ) : (
              <div className="flex gap-3">
                {showBackButton && (
                  <button
                    onClick={handleGoBack}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors duration-200 font-medium text-sm"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Go Back
                  </button>
                )}
                {showHomeButton && (
                  <button
                    onClick={handleGoHome}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600/80 hover:bg-cyan-600 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
                  >
                    <HomeIcon className="w-4 h-4" />
                    Go Home
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Security Note */}
        <p className="mt-4 text-center text-xs text-slate-600">
          This access attempt has been logged for security purposes.
        </p>
      </div>
    </div>
  );
});

PermissionDenied.displayName = 'PermissionDenied';

// ============================================================================
// Helper Functions
// ============================================================================

function generateDefaultMessage(
  requiredPermissions?: PermissionDeniedProps['requiredPermissions'],
  requiredRole?: PermissionDeniedProps['requiredRole'],
  currentRole?: PermissionDeniedProps['currentRole']
): string {
  if (requiredRole && currentRole) {
    return `This page requires ${ROLE_DISPLAY_NAMES[requiredRole]} privileges or higher. Your current role (${ROLE_DISPLAY_NAMES[currentRole]}) does not have sufficient access.`;
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    return 'You do not have the required permissions to access this resource. Please contact your administrator if you need access.';
  }

  return 'You do not have permission to access this page.';
}

// ============================================================================
// Inline Variant
// ============================================================================

interface PermissionDeniedInlineProps {
  message?: string;
  showIcon?: boolean;
  className?: string;
}

/**
 * PermissionDeniedInline - Compact inline version for use within pages
 */
export const PermissionDeniedInline: React.FC<PermissionDeniedInlineProps> = memo(({
  message = 'You do not have permission to perform this action.',
  showIcon = true,
  className = '',
}) => (
  <div
    className={`flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm ${className}`}
    role="alert"
  >
    {showIcon && (
      <ShieldExclamationIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
    )}
    <span className="text-red-200">{message}</span>
  </div>
));

PermissionDeniedInline.displayName = 'PermissionDeniedInline';

// ============================================================================
// Exports
// ============================================================================

export default PermissionDenied;

